import React, { useEffect, useState } from 'react';
import { FiShield, FiCheckCircle } from 'react-icons/fi';
import { staffJSON } from './staffApi';

export default function StaffAuthenticatorAccount({ compact = false, required = false, onComplete, onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [codes, setCodes] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const load = async signal => {
    const next = await staffJSON('/mfa/status', { signal });
    setStatus(next); onStatusChange?.(next);
  };
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal).catch(err => { if (!controller.signal.aborted) setError(err.message); });
    return () => controller.abort();
  }, []);
  const run = async action => {
    if (busy) return; setBusy(true); setError(''); setMessage('');
    try { await action(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const begin = () => run(async () => { setCode(''); setSetup(await staffJSON('/mfa/totp/setup', { method: 'POST' })); });
  const confirm = event => { event.preventDefault(); run(async () => {
    const result = await staffJSON('/mfa/totp/verify-setup', { method: 'POST', body: JSON.stringify({ code }) });
    setCodes(result.recoveryCodes); setSetup(null); setCode(''); await load();
  }); };
  const email = () => run(async () => {
    await staffJSON('/mfa/method', { method: 'POST', body: JSON.stringify({ method: 'email' }) });
    await load(); onComplete?.();
  });
  const disable = event => { event.preventDefault(); run(async () => {
    const result = await staffJSON('/mfa/totp/disable', { method: 'POST', body: JSON.stringify({ password, code }) });
    setPassword(''); setCode(''); setRemoving(false); setMessage(result.message); await load();
  }); };
  return <section className={`staff-authenticator${compact ? ' is-compact' : ''}`} aria-label="Staff sign-in security">
    <div className="staff-account-label"><FiShield /><strong>{required ? 'Choose how you verify staff sign-ins' : 'Sign-in security'}</strong></div>
    {error && <p role="alert" className="staff-inline-error">{error}</p>}
    {message && <p role="status" className="staff-inline-success">{message}</p>}
    {!status && !error && <p role="status">Checking sign-in security...</p>}
    {status && !codes && !setup && !removing && <>
      <p>{status.totp_enabled ? 'Authenticator app enabled for this staff account.' : 'Your staff sign-ins currently use email verification.'}</p>
      <p>Use the same app as the portal, such as Microsoft Authenticator or Google Authenticator. Add a separate <strong>Pipsqueak Staff</strong> entry; portal codes stay separate.</p>
      {!status.totp_enabled ? <div className="staff-actions">
        <button type="button" className="staff-primary" disabled={busy} onClick={begin}>Set up authenticator app</button>
        {required && <button type="button" disabled={busy} onClick={email}>Keep using email codes</button>}
      </div> : <button type="button" disabled={busy} onClick={() => { setRemoving(true); setCode(''); }}>Remove authenticator</button>}
    </>}
    {setup && <form onSubmit={confirm} className="staff-mfa-form">
      <p>Scan this QR code in your authenticator app, then enter its six-digit code.</p>
      <img className="staff-mfa-qr" src={setup.qr} alt="QR code for your Pipsqueak Staff authenticator" />
      <details><summary>Enter a setup key manually</summary><code className="staff-mfa-secret">{setup.secret}</code></details>
      <label>Authenticator code<input autoComplete="one-time-code" inputMode="numeric" value={code} onChange={e => setCode(e.target.value)} maxLength={6} required disabled={busy} /></label>
      <div className="staff-actions"><button type="submit" className="staff-primary" disabled={busy}>{busy ? 'Checking...' : 'Confirm authenticator'}</button><button type="button" disabled={busy} onClick={() => { setSetup(null); setCode(''); }}>Cancel setup</button></div>
    </form>}
    {codes && <>
      <p><FiCheckCircle /> Authenticator enabled. Save these recovery codes somewhere private. Each code works once if you lose your phone.</p>
      <div className="staff-recovery-codes">{codes.map(value => <code key={value}>{value}</code>)}</div>
      <button type="button" className="staff-primary" onClick={() => { setCodes(null); onComplete?.(); }}>I saved my recovery codes</button>
    </>}
    {removing && <form className="staff-mfa-form" onSubmit={disable}>
      <p>Confirm your password and a current app or recovery code. Future sign-ins will use email verification.</p>
      <label>Current password<input type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required disabled={busy} /></label>
      <label>App or recovery code<input autoComplete="one-time-code" value={code} onChange={e => setCode(e.target.value)} maxLength={40} required disabled={busy} /></label>
      <div className="staff-actions"><button type="submit" disabled={busy}>Confirm removal</button><button type="button" disabled={busy} onClick={() => { setRemoving(false); setPassword(''); setCode(''); }}>Cancel</button></div>
    </form>}
  </section>;
}
